'use client'

import { useEffect, useRef, useState } from 'react'
import { Keyboard } from 'types/keyboard'
import { cx } from 'utils/cx'

import type { SizeGuideRegion } from '../../../types'
import { RegionsButton } from '../commons/RegionsButton/RegionsButton'

import styles from './CustomDropdown.module.scss'
import text from 'fukku/styles/texts.module.scss'

type CustomDropdownProps = {
	readonly regions: SizeGuideRegion[]
	readonly value?: SizeGuideRegion
	readonly className?: string
	readonly onChange?: (region?: SizeGuideRegion) => void
}

export function CustomDropdown({
	regions,
	value,
	className,
	onChange,
}: CustomDropdownProps) {
	const dropdownRef = useRef<HTMLDivElement>(null)
	const buttonRef = useRef<HTMLButtonElement>(null)
	const [isOpen, setIsOpen] = useState(false)
	const [selectedRegion, setSelectedRegion] = useState<
		SizeGuideRegion | undefined
	>(value)

	const handleChange = (newValue: SizeGuideRegion) => {
		setSelectedRegion(newValue)
		onChange?.(newValue)
		setIsOpen(false)
	}

	useEffect(() => {
		if (selectedRegion && regions) {
			const newValue = regions.find((region) => region === selectedRegion)
			setSelectedRegion(newValue)
		}
	}, [value, regions])

	useEffect(() => {
		const onOutsideClick = (e: MouseEvent) => {
			if (
				isOpen &&
				dropdownRef.current &&
				!dropdownRef.current.contains(e.target as Node)
			) {
				setIsOpen(false)
				buttonRef.current?.focus()
			}
		}

		const onKeyDown = ({ key: keyPressed }: KeyboardEvent) => {
			if (keyPressed === Keyboard.Escape) {
				setIsOpen(false)
				buttonRef.current?.focus()
			}

			const upOrDown =
				keyPressed === Keyboard.ArrowUp || keyPressed === Keyboard.ArrowDown

			if (upOrDown && !isOpen) {
				setIsOpen(true)
			}
		}

		document.addEventListener('mousedown', onOutsideClick)
		document.addEventListener('keydown', onKeyDown)

		return () => {
			document.removeEventListener('mousedown', onOutsideClick)
			document.removeEventListener('keydown', onKeyDown)
		}
	}, [isOpen])

	return (
		<div ref={dropdownRef} className={cx(styles.wrapper, className)}>
			<RegionsButton
				ref={buttonRef}
				label={selectedRegion || regions[0]}
				isOpen={isOpen}
				onClick={() => setIsOpen(!isOpen)}
				dataTestId='sizeGuide.regions.selector'
			/>
			{isOpen && (
				<div className={styles.dropdown} lang='en' aria-label='Dropdown menu'>
					<ul className={styles.list}>
						{regions.map((region) => (
							<li key={region} className={styles.item}>
								<button
									data-testid='sizeguide.regions.option'
									onClick={() => handleChange(region)}
									className={cx(styles.itemButton, text.bodyM)}
								>
									{region}
								</button>
							</li>
						))}
					</ul>
				</div>
			)}
		</div>
	)
}
